
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import List from '@/common/components/UI/List/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'

// module components
import RPKListItem from '@/modules/RPKModule/components/RPKListItem/index.vue'
import RPKFilters from '@/modules/RPKModule/components/RPKFilters/index.vue'

// types
import { RPKListParamsT, RPKT } from '@/core/types/rpk.types'

// realisations
import { rpk } from '@/core/realisations/rpk.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// composable
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        list: List,
        'the-button': TheButton,
        'rpk-list-item': RPKListItem,
        'rpk-filters': RPKFilters,
        NewPagination,
        NewLimit,
    },
    setup() {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_export_loading: false,
        })

        const data_state = reactive({
            rpk: new DataState<RPKT>({}),
        })

        const filter_state = reactive({
            limit: 50,
            id: undefined as number | undefined,
            ftl_order_id: undefined as number | undefined,
            turnover_date_from: undefined as string | undefined,
            turnover_date_to: undefined as string | undefined,
            type: undefined as number | undefined,
            company_id: undefined as number | undefined,
            act_number: undefined as string | undefined,
            created_at_from: undefined as string | undefined,
            act_date_from: undefined as string | undefined,
            act_date_to: undefined as string | undefined,
            status_id: undefined as number | undefined,
            pbc_date_from: undefined as string | undefined,
            pbc_date_to: undefined as string | undefined,
        })

        const loadRPKList = async (page: number) => {
            data_state.rpk.pagination.current_page = page

            // filters
            const params = {
                page,
                ...filter_state,
            }

            try {
                state.is_loading = true
                const response = await rpk.getRPKList(params)

                data_state.rpk.list = response.data
                data_state.rpk.pagination.total_pages = response.meta.last_page
                data_state.rpk.pagination.total_items = response.meta.total

                data_state.rpk.is_failed = false
                data_state.rpk.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.rpk.failure.catchError(error)

                if (!data_state.rpk.failure.error_state.is_cancel) {
                    data_state.rpk.is_failed = true
                }
            } finally {
                if (!data_state.rpk.failure.error_state.is_cancel) {
                    data_state.rpk.is_loading = false
                }

                state.is_loading = false
            }
        }

        const onSearch = (rpk: RPKListParamsT) => {
            filter_state.id = rpk.id
            filter_state.ftl_order_id = rpk.ftl_order_id
            filter_state.turnover_date_from = rpk.turnover_date_from
            filter_state.turnover_date_to = rpk.turnover_date_to
            filter_state.type = rpk.type
            filter_state.company_id = rpk.company_id
            filter_state.act_number = rpk.act_number
            filter_state.created_at_from = rpk.created_at_from
            filter_state.act_date_from = rpk.act_date_from
            filter_state.act_date_to = rpk.act_date_to
            filter_state.status_id = rpk.status_id
            filter_state.pbc_date_from = rpk.pbc_date_from
            filter_state.pbc_date_to = rpk.pbc_date_to

            loadRPKList(1)
        }

        const onReset = () => {
            filter_state.id = undefined
            filter_state.ftl_order_id = undefined
            filter_state.turnover_date_from = undefined
            filter_state.turnover_date_to = undefined
            filter_state.type = undefined
            filter_state.company_id = undefined
            filter_state.act_number = undefined
            filter_state.created_at_from = undefined
            filter_state.act_date_from = undefined
            filter_state.act_date_to = undefined
            filter_state.status_id = undefined
            filter_state.pbc_date_from = undefined
            filter_state.pbc_date_to = undefined

            loadRPKList(1)
        }

        const saveFile = (content: File | Blob, file_name: string) => {
            const download_url = window.URL.createObjectURL(content)

            const link = document.createElement('a')
            link.href = download_url
            link.setAttribute('download', file_name)
            document.body.appendChild(link)
            link.click()
            link.remove()
        }

        const exportRPK = async () => {
            try {
                state.is_export_loading = true

                const response = await rpk.exportExcel({
                    pbc_date_from: filter_state.pbc_date_from,
                    pbc_date_to: filter_state.pbc_date_to,
                    company_id: filter_state.company_id,
                })

                let fileName = 'РПК'

                if (filter_state.pbc_date_from) {
                    fileName += ` ${filter_state.pbc_date_from}`
                }

                if (filter_state.pbc_date_to) {
                    fileName += `- ${filter_state.pbc_date_to}`
                }

                saveFile(response, `${fileName}.xlsx`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При экспорте РПК произошла ошибка' })
            } finally {
                state.is_export_loading = false
            }
        }

        const deleteRPK = async (rpk_id: number) => {
            try {
                state.is_loading = true

                await rpk.deleteRPK(rpk_id)

                notifications.notify({
                    title: `РПК (ID: ${rpk_id}) удалено`,
                    type: 'success',
                })
                loadRPKList(1)
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: `Ошибка при удалении РПК (ID: ${rpk_id})`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const onLimitChange = (limit: number) => {
            filter_state.limit = limit
            loadRPKList(data_state.rpk.pagination.current_page)
        }

        const onChangePage = (page: number) => {
            loadRPKList(page)
        }

        onBeforeMount(() => {
            loadRPKList(1)
        })

        return {
            state,
            data_state,
            filter_state,
            // RPK
            loadRPKList,
            onSearch,
            onReset,
            exportRPK,
            deleteRPK,
            onLimitChange,
            onChangePage,
        }
    },
})
